import { Descriptions, Form, Input, InputNumber, Modal, Radio, notification } from 'antd';
import { useEffect, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { LedgerRow, StarAccountRow } from 'pages/star-shared/types';
import { mapStarError } from 'pages/star-shared/star-error';
import { formatNumber, personName } from 'pages/star-shared/utils';
import StarAmount from 'pages/star-shared/components/star-amount';
import { StarGroupTag } from 'pages/star-shared/components/status-tags';

interface Props {
  account: StarAccountRow | null;
  onClose: () => void;
  onSuccess: (userId: number, row: LedgerRow | null) => void;
}

interface AdjustForm {
  direction: 'add' | 'sub';
  amount: number;
  reason: string;
}

// Cộng/trừ Sao thủ công: POST /stars/admin/adjust { userId, amount (≠0), reason }.
const AdjustModal = ({ account, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm<AdjustForm>();
  const [loading, setLoading] = useState(false);
  const open = !!account;

  useEffect(() => {
    if (open) form.setFieldsValue({ direction: 'add', amount: undefined as any, reason: '' });
  }, [open, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: AdjustForm) => {
    if (!account) return;
    const amount = values.direction === 'sub' ? -Math.abs(values.amount) : Math.abs(values.amount);
    setLoading(true);
    try {
      const res: any = await AxiosUtils.post(API.starsAdjust, {
        userId: account.userId,
        amount,
        reason: values.reason.trim(),
      });
      const row: LedgerRow | null = res?.data || null;
      notification.success({
        message: `Đã ${amount > 0 ? 'cộng' : 'trừ'} ${formatNumber(Math.abs(amount))} ⭐ cho ${personName({
          id: account.userId,
          nickName: account.nickName,
          username: account.username,
        })}.`,
        description:
          typeof row?.balanceAfter === 'number'
            ? `Số dư mới: ${formatNumber(row.balanceAfter)} ⭐`
            : undefined,
      });
      onSuccess(account.userId, row);
      handleClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Cộng/trừ Sao"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      {account && (
        <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Người dùng">
            {personName({ id: account.userId, nickName: account.nickName, username: account.username })}
            {account.username ? ` (${account.username})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Nhóm">
            <StarGroupTag value={account.starGroup} />
          </Descriptions.Item>
          <Descriptions.Item label="Số dư hiện tại">
            <StarAmount value={account.balance} />
          </Descriptions.Item>
        </Descriptions>
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="custom-form"
        initialValues={{ direction: 'add' }}
      >
        <Form.Item label="Thao tác" name="direction">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              { value: 'add', label: 'Cộng Sao' },
              { value: 'sub', label: 'Trừ Sao' },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="Số Sao"
          name="amount"
          rules={[
            { required: true, message: 'Vui lòng nhập số Sao.' },
            { type: 'integer', min: 1, message: 'Số Sao phải là số nguyên lớn hơn 0.' },
          ]}
        >
          <InputNumber size="large" style={{ width: '100%' }} min={1} precision={0} placeholder="VD: 50" />
        </Form.Item>
        <Form.Item
          label="Lý do"
          name="reason"
          rules={[
            { required: true, whitespace: true, message: 'Vui lòng nhập lý do.' },
            { max: 255, message: 'Lý do tối đa 255 ký tự.' },
          ]}
          extra="Bắt buộc. MOD bị giới hạn mức điều chỉnh mỗi lần theo cấu hình; không tự cộng cho chính mình."
        >
          <Input.TextArea rows={3} maxLength={255} showCount placeholder="VD: Thưởng nóng sự kiện 20/10" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton text="Xác nhận" bgColor="secondary-green" htmlType="submit" loading={loading} />
        </div>
      </Form>
    </Modal>
  );
};

export default AdjustModal;
